import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Plus, Search, Filter, UserPlus, Mail, Phone, MapPin,
  Calendar, DollarSign, Building2, ChevronRight, MoreHorizontal, 
  Check, Clock, AlertCircle, Target, TrendingUp, FileText, 
  MessageSquare, CheckCircle2, Trash2, Archive, RefreshCw, Home, 
  Video, Send, Edit2, User, Briefcase, Star, X, ExternalLink,
  FileSignature, Printer, PenTool, Settings2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import HelpIcon from '@/components/HelpIcon';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/utils/apiError';
import QuickStartWizard from '@/components/QuickStartWizard';
import AppShell from '@/components/layout/AppShell';
import { getCompanySettings } from '@/utils/companySettings';
import MastheadBand from '@/components/dashboard/MastheadBand';
import '@/styles/kmd-sheet.css';
import '@/styles/pages/pipeline.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// DP117 item 6 — column colour comes from the STAGE ID, not from the API's
// `color` field: migration 0079 wrote the old platform hex values into the
// stage documents and they are not Chad's palette. Values live in
// styles/pages/pipeline.css; nothing raw enters this file.
const STAGE_COLOURS = {
  inquiry: 'var(--st-inquiry)',
  consultation: 'var(--st-consultation)',
  design_agreement: 'var(--st-agreement)',
  signed: 'var(--st-signed)',
  following_up: 'var(--st-following)',
  not_hired: 'var(--st-nothired)',
};
const stageColour = (id) => STAGE_COLOURS[id] || 'var(--km-ink-3)';

// Small counts read as words in the masthead sentence, the way a person says
// them. Above twelve the numeral is clearer.
const WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven',
  'eight', 'nine', 'ten', 'eleven', 'twelve'];
const say = (n) => (n <= 12 ? WORDS[n] : String(n));
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// Visuals for won/lost (terminal pseudo-stages + stat cards)
const WON_VISUAL = { color: 'bg-emerald-500', textColor: 'text-emerald-700', bgLight: 'bg-emerald-50', icon: CheckCircle2 };
const LOST_VISUAL = { color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50', icon: X };

// Palette cycled across CONFIGURED stages (board is config-driven; see
// decorateStages). Order roughly mirrors a design-firm funnel.
const STAGE_VISUALS = [
  { color: 'bg-slate-500', textColor: 'text-slate-700', bgLight: 'bg-slate-50', icon: MessageSquare },
  { color: 'bg-blue-500', textColor: 'text-blue-700', bgLight: 'bg-blue-50', icon: Phone },
  { color: 'bg-amber-500', textColor: 'text-amber-700', bgLight: 'bg-amber-50', icon: FileText },
  { color: 'bg-purple-500', textColor: 'text-purple-700', bgLight: 'bg-purple-50', icon: PenTool },
  { color: 'bg-cyan-600', textColor: 'text-cyan-700', bgLight: 'bg-cyan-50', icon: FileSignature },
  { color: 'bg-orange-500', textColor: 'text-orange-700', bgLight: 'bg-orange-50', icon: Building2 },
  { color: 'bg-teal-600', textColor: 'text-teal-700', bgLight: 'bg-teal-50', icon: Target },
  { color: 'bg-indigo-500', textColor: 'text-indigo-700', bgLight: 'bg-indigo-50', icon: Star },
];

// Fallback (pre-config / config-fetch failure only). Matches the old
// hardcoded board so the page still renders something sensible offline.
const FALLBACK_STAGES = [
  { id: 'inquiry', label: 'Inquiry', ...STAGE_VISUALS[0] },
  { id: 'consultation', label: 'Consultation', ...STAGE_VISUALS[1] },
  { id: 'proposal', label: 'Proposal', ...STAGE_VISUALS[2] },
  { id: 'closing', label: 'Closing', ...STAGE_VISUALS[3] },
  { id: 'won', label: 'Won', terminal: true, pseudo: true, ...WON_VISUAL },
  { id: 'lost', label: 'Lost', terminal: true, pseudo: true, ...LOST_VISUAL },
];

const TERMINAL_IDS = ['won', 'lost'];

// Build the UI stage list from /crm/pipeline/stages config. Won/Lost stay
// available as stat-filter pseudo-stages (and render targets for legacy
// leads) even when not configured; `pseudo` keeps them out of move menus.
const decorateStages = (configStages) => {
  const stages = (configStages || []).map((s, i) => ({
    id: s.id,
    label: s.name || s.id,
    terminal: TERMINAL_IDS.includes(s.id) || Boolean(s.is_terminal),
    // DP117 items 6, 7 and 9 — carried through from the stage document:
    // `role` so Won/Lost select by role rather than by the ids 'won'/'lost'
    // that this tenant no longer has, `rule` so each column head prints the
    // description 0079 wrote rather than one composed here, and `colour`
    // from the id map above.
    role: s.role || null,
    rule: s.description || '',
    order: typeof s.order === 'number' ? s.order : i,
    colour: stageColour(s.id),
    ...STAGE_VISUALS[i % STAGE_VISUALS.length],
    ...(s.id === 'won' ? WON_VISUAL : {}),
    ...(s.id === 'lost' ? LOST_VISUAL : {}),
  })).sort((a, b) => a.order - b.order);
  TERMINAL_IDS.forEach((t) => {
    if (!stages.some((s) => s.id === t)) {
      stages.push({
        id: t,
        label: t === 'won' ? 'Won' : 'Lost',
        terminal: true,
        pseudo: true,
        rule: '',
        colour: stageColour(t),
        ...(t === 'won' ? WON_VISUAL : LOST_VISUAL),
      });
    }
  });
  return stages;
};

const LEAD_SOURCES = [
  'Calendly', 'Website', 'Referral', 'Social Media', 'Houzz', 'Instagram', 
  'Word of Mouth', 'Trade Show', 'Advertisement', 'Other'
];

// Stage-aware nudge thresholds (days since last activity)
const NUDGE_THRESHOLDS = {
  inquiry: 2,       // Hot leads — respond fast
  consultation: 5,  // Active engagement
  proposal: 7,      // Client deciding
  closing: 10,      // Longer negotiation
};

// Compute days since last activity for a lead
const getLeadActivity = (lead) => {
  const dates = [
    lead.last_contact_date,
    lead.updated_at,
    lead.created_at,
  ].filter(Boolean).map(d => new Date(d));
  
  // Also check timeline entries
  if (lead.timeline && Array.isArray(lead.timeline)) {
    lead.timeline.forEach(entry => {
      if (entry.timestamp) dates.push(new Date(entry.timestamp));
    });
  }
  
  if (dates.length === 0) return { daysSince: 999, needsNudge: false };
  
  const latest = new Date(Math.max(...dates.map(d => d.getTime())));
  const daysSince = Math.floor((Date.now() - latest.getTime()) / (1000 * 60 * 60 * 24));
  const threshold = NUDGE_THRESHOLDS[lead.stage] || 7;
  const needsNudge = !['won', 'lost'].includes(lead.stage) && daysSince >= threshold;
  
  return { daysSince, needsNudge, threshold };
};

const PROJECT_TYPES = [
  'New Construction', 'Renovation', 'Addition', 'Interior Design Only',
  'Kitchen Remodel', 'Bathroom Remodel', 'Whole House', 'Commercial'
];

const BUDGET_RANGES = [
  'Under $50K', '$50K - $100K', '$100K - $250K', '$250K - $500K', '$500K - $1M', '$1M+'
];

// Legacy db.leads statuses → canonical stage ids. buildStageMapper snaps the
// result to the CONFIGURED stage ids (config-driven replacement for the old
// hardcoded STATUS_TO_STAGE table).
const LEGACY_STATUS_MAP = {
  'new': 'inquiry',
  'contacted': 'consultation',
  'qualified': 'proposal',
  'proposal_sent': 'proposal',
  'negotiating': 'closing',
  'converted': 'won',
  'discovery': 'consultation',
  'negotiation': 'closing',
  'archived': 'lost'
};

const buildStageMapper = (stages) => {
  const ids = new Set(stages.map((s) => s.id));
  const first = stages.find((s) => !s.terminal)?.id || 'inquiry';
  return (value) => {
    if (!value) return first;
    if (ids.has(value)) return value;            // already a configured/known id
    const legacy = LEGACY_STATUS_MAP[value];
    if (legacy && ids.has(legacy)) return legacy;
    // Unknown/unconfigured: keep the raw value — List view renders its name
    // and data_integrity.crm_leads flags it (never silently re-bucket).
    return legacy || value;
  };
};

export default function PipelinePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [pipelineStages, setPipelineStages] = useState(FALLBACK_STAGES);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('kanban');
  // DP117 item 7 — was 'active', which filtered terminal-stage leads out of
  // the board, so the Signed and Not Hired columns rendered empty even when
  // they held leads. The board opens on every configured stage and every lead.
  const [activeFilter, setActiveFilter] = useState('all'); // all, active, won, lost
  const [tenantName, setTenantName] = useState('');
  const [dragOver, setDragOver] = useState(null);
  
  // Selected lead for detail panel
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  
  // New lead dialog
  const [showNewLeadDialog, setShowNewLeadDialog] = useState(false);
  const [newLead, setNewLead] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    source: '',
    project_type: '',
    budget_range: '',
    notes: '',
    property_address: ''
  });
  
  // Action dialogs
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  // PL-1 — the New Project wizard, opened from a lead reaching Signed.
  const [showProjectWizard, setShowProjectWizard] = useState(false);
  const [wizardPrefill, setWizardPrefill] = useState(null);
  const [wizardLead, setWizardLead] = useState(null);
  const [projectNames, setProjectNames] = useState({});
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  
  // Dark mode
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('keyMadeDarkMode');
    return saved === 'true';
  });

  useEffect(() => {
    const handleDarkModeChange = () => {
      const saved = localStorage.getItem('keyMadeDarkMode');
      setDarkMode(saved === 'true');
    };
    window.addEventListener('storage', handleDarkModeChange);
    const interval = setInterval(handleDarkModeChange, 2000);
    return () => {
      window.removeEventListener('storage', handleDarkModeChange);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    fetchAllLeads();
    getCompanySettings()
      .then((d) => setTenantName(d?.company_name || ''))
      .catch(() => setTenantName(''));
  }, []);
  
  // Handle URL action parameter for opening new lead dialog
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'new-lead') {
      setShowNewLeadDialog(true);
      // Clear the action param from URL
      searchParams.delete('action');
      navigate(`/pipeline?${searchParams.toString()}`, { replace: true });
    }
  }, [searchParams, navigate]);

  // Fetch leads from both collections and merge
  const fetchAllLeads = async () => {
    try {
      setLoading(true);

      // Config drives the board: fetch stages FIRST so lead normalization
      // snaps onto the configured ids (was a hardcoded 6-stage list).
      let stages = FALLBACK_STAGES;
      try {
        const cfgRes = await axios.get(`${API}/crm/pipeline/stages`);
        const cfgStages = cfgRes.data?.stages || (Array.isArray(cfgRes.data) ? cfgRes.data : []);
        if (cfgStages.length) stages = decorateStages(cfgStages);
      } catch (err) {
        console.warn('Pipeline config unavailable — using fallback stages', err);
      }
      setPipelineStages(stages);
      const mapStage = buildStageMapper(stages);

      // Fetch from both endpoints
      const [leadsRes, crmRes] = await Promise.all([
        axios.get(`${API}/leads`).catch((err) => {
          toast.error(getApiErrorMessage(err, 'Failed to load legacy leads'));
          return { data: [] };
        }),
        axios.get(`${API}/crm/leads`).catch((err) => {
          toast.error(getApiErrorMessage(err, 'Failed to load CRM leads'));
          return { data: [] };
        })
      ]);

      // GET /api/leads returns a raw array (leads.py:59). Previous code read
      // leadsRes.data.leads which was always undefined → leads in db.leads
      // never rendered in the pipeline. Normalize to the raw-array shape.
      const oldLeads = (leadsRes.data || []).map(lead => ({
        ...lead,
        stage: mapStage(lead.status),
        source_type: 'legacy',
        display_name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || lead.name || 'Unknown'
      }));

      const crmLeads = (crmRes.data || []).map(lead => ({
        ...lead,
        stage: mapStage(lead.stage),
        source_type: 'crm',
        display_name: lead.name || 'Unknown'
      }));

      // Merge and dedupe by email
      const emailMap = new Map();
      [...crmLeads, ...oldLeads].forEach(lead => {
        const key = lead.email?.toLowerCase() || lead.id;
        if (!emailMap.has(key)) {
          emailMap.set(key, lead);
        }
      });

      const merged = Array.from(emailMap.values());
      setLeads(merged);

      // PL-1 — the leads that became projects show which one, by name.
      if (merged.some(l => l.converted_project_id)) {
        try {
          const projects = await axios.get(`${API}/projects`);
          setProjectNames(Object.fromEntries(
            (projects.data || []).map(p => [p.id, p.name])));
        } catch (e) {
          // a name we cannot read is shown as "open" — never invented
        }
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to load pipeline data');
    } finally {
      setLoading(false);
    }
  };

  const createLead = async () => {
    if (!newLead.name || !newLead.email) {
      toast.error('Name and email are required');
      return;
    }

    try {
      // Create in CRM system (primary)
      const res = await axios.post(`${API}/crm/leads`, {
        ...newLead,
        stage: 'new'
      });

      setLeads([{
        ...res.data,
        stage: 'new',
        source_type: 'crm',
        display_name: newLead.name
      }, ...leads]);
      
      setShowNewLeadDialog(false);
      setNewLead({
        name: '', email: '', phone: '', company: '', source: '',
        project_type: '', budget_range: '', notes: '', property_address: ''
      });
      toast.success('Lead added to pipeline');
    } catch (error) {
      toast.error('Failed to create lead');
    }
  };

  // PL-1 — Signed is ONE step. Wherever a lead is moved there — a dragged
  // card, the card's own control, the lead page — it comes through here: the
  // stage is set, and the New Project wizard opens pre-filled from the lead.
  // Choosing the workflow template stays the user's task, and the project is
  // only linked to the lead once the user creates it.
  const moveToSigned = async (lead, stageId) => {
    const moved = await updateLeadStage(lead, stageId);
    if (!moved) return;
    const surname = (lead.display_name || lead.name || '').trim().split(/\s+/).pop() || '';
    const work = lead.project_type
      || (lead.project_types || [])[0]
      || lead.project_description
      || 'Project';
    const worksheet = lead.consultation_worksheet || {};
    setWizardLead(lead);
    setWizardPrefill({
      name: [surname, work].filter(Boolean).join(' - '),
      client_name: lead.display_name || lead.name || '',
      client_email: lead.email || '',
      client_phone: lead.phone || '',
      address: lead.property_address || lead.site_address || '',
      description: lead.project_description || lead.notes || '',
      project_type: lead.project_type || (lead.project_types || [])[0] || 'residential',
      estimated_budget: worksheet.agreed_investment || worksheet.agreed_target || '',
      lead_id: lead.id,
    });
    setShowProjectWizard(true);
  };

  const updateLeadStage = async (lead, newStage) => {
    try {
      if (lead.source_type === 'crm') {
        // Stage ids are the configured ids now — send as-is; the backend
        // validates the destination and explains rejections.
        await axios.patch(`${API}/crm/leads/${lead.id}`, { stage: newStage });
      } else {
        // Map stage back to old status format for legacy leads
        const statusMap = {
          'inquiry': 'new',
          'consultation': 'contacted',
          'proposal': 'proposal_sent',
          'closing': 'negotiating',
          'won': 'converted',
          'lost': 'lost'
        };
        await axios.put(`${API}/leads/${lead.id}`, { status: statusMap[newStage] || newStage });
      }

      setLeads(leads.map(l => l.id === lead.id ? { ...l, stage: newStage } : l));
      
      if (selectedLead?.id === lead.id) {
        setSelectedLead({ ...selectedLead, stage: newStage });
      }

      const stageLabel = pipelineStages.find(s => s.id === newStage)?.label || newStage;
      toast.success(`Moved to ${stageLabel}`);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stage');
      return false;
    }
  };

  const convertToProject = async (lead) => {
    try {
      let res;
      if (lead.source_type === 'crm') {
        res = await axios.post(`${API}/crm/leads/${lead.id}/convert`);
      } else {
        res = await axios.post(`${API}/leads/${lead.id}/convert-to-project`);
      }

      toast.success(`Project created: ${res.data.project_code || 'Success'}`);
      setShowConvertDialog(false);
      setShowDetailPanel(false);
      
      // Update lead stage locally
      setLeads(leads.map(l => l.id === lead.id ? { ...l, stage: 'won' } : l));
      
      // Navigate to new project
      navigate(`/projects/${res.data.project_id}/hub`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert lead');
    }
  };

  const deleteLead = async (lead) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    
    try {
      if (lead.source_type === 'crm') {
        await axios.delete(`${API}/crm/leads/${lead.id}`);
      } else {
        await axios.delete(`${API}/leads/${lead.id}`);
      }

      setLeads(leads.filter(l => l.id !== lead.id));
      setShowDetailPanel(false);
      toast.success('Lead deleted');
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const archiveLead = async (lead) => {
    await updateLeadStage(lead, 'lost');
  };

  // Filter leads based on active filter
  const getFilteredLeads = () => {
    let filtered = leads;

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(lead => 
        lead.display_name?.toLowerCase().includes(query) ||
        lead.email?.toLowerCase().includes(query) ||
        lead.company?.toLowerCase().includes(query) ||
        lead.property_address?.toLowerCase().includes(query)
      );
    }

    // Apply status filter. DP117 item 7 — Won and Lost select by the stage's
    // ROLE ("hired" / "lost"), because the literal ids 'won' and 'lost' do not
    // exist on this tenant since 0079.
    const terminalIds = pipelineStages.filter(s => s.terminal).map(s => s.id);
    const hiredIds = pipelineStages.filter(s => s.role === 'hired' || s.id === 'won').map(s => s.id);
    const lostIds = pipelineStages.filter(s => s.role === 'lost' || s.id === 'lost').map(s => s.id);
    switch (activeFilter) {
      case 'active':
        filtered = filtered.filter(l => !terminalIds.includes(l.stage));
        break;
      case 'won':
        filtered = filtered.filter(l => hiredIds.includes(l.stage));
        break;
      case 'lost':
        filtered = filtered.filter(l => lostIds.includes(l.stage));
        break;
      default:
        break;
    }

    return filtered;
  };

  const filteredLeads = getFilteredLeads();

  // Get leads by stage for Kanban
  const getLeadsByStage = (stageId) => {
    // PL-1 — once the project exists the lead has left the board. It stays in
    // the lead list, with the project it became.
    return filteredLeads.filter(l => l.stage === stageId && !l.converted_project_id);
  };

  // Calculate stats. DP117 item 7 — won/lost count by the stage's ROLE, so
  // the figures follow this tenant's `signed` and `not_hired` rather than the
  // ids 'won' and 'lost' it no longer has.
  const terminalStageIds = pipelineStages.filter(s => s.terminal).map(s => s.id);
  const hiredStageIds = pipelineStages.filter(s => s.role === 'hired' || s.id === 'won').map(s => s.id);
  const lostStageIds = pipelineStages.filter(s => s.role === 'lost' || s.id === 'lost').map(s => s.id);
  const wonCount = leads.filter(l => hiredStageIds.includes(l.stage)).length;
  const stats = {
    total: leads.length,
    active: leads.filter(l => !terminalStageIds.includes(l.stage)).length,
    won: wonCount,
    lost: leads.filter(l => lostStageIds.includes(l.stage)).length,
    conversionRate: leads.length > 0
      ? Math.round((wonCount / leads.length) * 100)
      : 0,
    pipelineValue: leads.reduce((sum, l) => {
      const budgetMatch = (l.budget_range || l.budget)?.match(/\d+/g);
      return sum + (budgetMatch ? parseInt(budgetMatch[0]) * 1000 : 0);
    }, 0)
  };

  // The masthead sentence. Every figure comes from the leads already in
  // state; when nothing needs anything it is still a sentence.
  const countIn = (id) => leads.filter(l => l.stage === id).length;
  const mastSentence = (() => {
    if (!leads.length) return 'No leads yet.';
    const head = `${cap(say(leads.length))} lead${leads.length === 1 ? '' : 's'}`;
    const notes = [
      [countIn('design_agreement'), 'waiting on an agreement'],
      [countIn('inquiry'), 'just arrived'],
      [countIn('following_up'), 'gone quiet'],
    ].filter(([n]) => n > 0);
    if (!notes.length) return `${head}, nothing waiting on you.`;
    const [n, phrase] = notes[0];
    return `${head}, ${say(n)} ${phrase}.`;
  })();
  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric'
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  // Save individual lead field (used by detail panel inline editing)
  const saveLeadField = async (lead, field, value) => {
    try {
      if (lead.source_type === 'crm') {
        await axios.patch(`${API}/crm/leads/${lead.id}`, { [field]: value });
      } else {
        await axios.put(`${API}/leads/${lead.id}`, { [field]: value });
      }
      // Update in leads array
      setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, [field]: value } : l));
    } catch (error) {
      console.error('Error saving lead field:', error);
      toast.error('Failed to save');
    }
  };

  // Drag and drop handlers
  const [draggedLead, setDraggedLead] = useState(null);
  
  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, stageId) => {
    e.preventDefault();
    if (draggedLead && draggedLead.stage !== stageId) {
      if (hiredStageIds.includes(stageId) && !draggedLead.converted_project_id) {
        moveToSigned(draggedLead, stageId);
      } else {
        updateLeadStage(draggedLead, stageId);
      }
    }
    setDraggedLead(null);
    setDragOver(null);
  };

  // Dark mode styling
  const bgClass = darkMode ? 'bg-[#0D0D0D]' : 'bg-[#FAF9F7]';
  const cardBg = darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-white border-gray-200';
  const headerBg = darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : 'bg-white border-gray-200';
  const textClass = darkMode ? 'text-[#E5E2DD]' : 'text-gray-900';
  const mutedClass = darkMode ? 'text-[#8A8A8A]' : 'text-gray-500';
  const inputClass = darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A] text-[#E5E2DD]' : '';

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${bgClass}`}>
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-[#BC5D41] mx-auto mb-4" />
          <p className={mutedClass}>Loading pipeline...</p>
        </div>
      </div>
    );
  }

  // DP117 item 2 — the page's own tan header and its own background are gone:
  // the surface belongs to the shell, and AppShell already renders .km-panel
  // around its children, so nothing here adds a panel.
  const pageActions = (
    <div className="flex items-center gap-3">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/admin/settings?tab=pipeline')}
        data-testid="manage-stages-link"
        title="Edit pipeline stage configuration"
      >
        <Settings2 className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
        Manage stages
      </Button>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
        <Input
          placeholder="Search leads..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 w-56"
          data-testid="pipeline-search"
        />
      </div>
      <div className="flex rounded-lg p-1 bg-muted">
        <Button variant={viewMode === 'kanban' ? 'default' : 'ghost'} size="sm"
                onClick={() => setViewMode('kanban')} className="h-7 px-3"
                data-testid="pipeline-view-board">Board</Button>
        <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="sm"
                onClick={() => setViewMode('list')} className="h-7 px-3"
                data-testid="pipeline-view-list">List</Button>
      </div>
      <Button onClick={() => setShowNewLeadDialog(true)} data-testid="new-lead-btn">
        <Plus className="w-4 h-4 mr-2" />
        New Lead
      </Button>
    </div>
  );

  return (
    <AppShell page="pipeline" shellTestId="pipeline-shell" breadcrumbTail="Lead Pipeline"
              topStripActions={pageActions}>
    <div data-testid="pipeline-page" style={{ padding: 'var(--km-s5)' }}>
      {/* DP139 — Pipeline is a tenant surface, so it wears the app's shared
          Branding-aware band rather than the hand-written one DP117 gave it.
          The sentence, the subline and the three figures are unchanged; only
          the thing drawing them changed. `page="pipeline"` has no Branding
          entry of its own yet, so it inherits My Desk's picture, which is the
          intended behaviour until Chad configures it. */}
      <MastheadBand
        page="pipeline"
        testId="pipeline-masthead"
        darkMode={darkMode}
        greeting={null}
        sentence={{ pre: mastSentence }}
        subline={['Lead Pipeline', tenantName, today].filter(Boolean).join(' \u00B7 ')}
        figures={[
          { label: 'Leads', value: stats.total },
          { label: 'Active', value: stats.active },
          { label: 'Signed', value: stats.won },
        ]}
      />

      <div>
        {/* DP117 item 4 — the five separate rounded tiles are one .kmd-sheet
            card carrying the series mark, the same object as Dashboard's A.1.
            Same five figures, and the three that were filters still filter. */}
        <article
          className="kmd-sheet mb-6"
          style={{ borderTopWidth: '2px', borderTopColor: 'var(--st-following)' }}
          data-testid="card-A1-pipeline"
        >
          <div className="kmd-sheet__block">
            <span className="kmd-sheet__series" style={{ color: 'var(--st-following)' }}>A.1</span>
            <span className="kmd-sheet__name">The pipeline in figures</span>
            <span className="kmd-sheet__meta" data-testid="card-A1-count">
              {stats.total} lead{stats.total === 1 ? '' : 's'}
            </span>
          </div>
          <div className="kmd-sheet__body">
            <div className="kpp-five">
              <button type="button" className="text-left" onClick={() => setActiveFilter('all')}
                      aria-pressed={activeFilter === 'all'} data-testid="stat-total">
                <div className="kpp-mm"><div><b>{stats.total}</b><span>Total Leads</span></div></div>
              </button>
              <button type="button" className="text-left" onClick={() => setActiveFilter('active')}
                      aria-pressed={activeFilter === 'active'} data-testid="stat-active">
                <div className="kpp-mm"><div><b>{stats.active}</b><span>Active</span></div></div>
              </button>
              <button type="button" className="text-left" onClick={() => setActiveFilter('won')}
                      aria-pressed={activeFilter === 'won'} data-testid="stat-won">
                <div className="kpp-mm"><div><b>{stats.won}</b><span>Won</span></div></div>
              </button>
              <div data-testid="stat-conversion">
                <div className="kpp-mm"><div><b>{stats.conversionRate}%</b><span>Conversion</span></div></div>
              </div>
              <div data-testid="stat-value">
                <div className="kpp-mm"><div><b>${(stats.pipelineValue / 1000).toFixed(0)}K</b><span>Pipeline Value</span></div></div>
              </div>
            </div>
          </div>
        </article>

        {/* Kanban View — DP117 item 5. The board markup is the mockup's.
            Drag and drop is untouched: same handleDragStart, handleDragOver,
            handleDrop and the same data-testid on every column. */}
        {viewMode === 'kanban' && (
          <div className="kpp-board" data-testid="pipeline-board">
            {pipelineStages
              // DP117 item 7 — every CONFIGURED stage is a column, in its
              // `order`, terminal ones included. A pseudo stage appended by
              // decorateStages ('won', 'lost') is not a column.
              .filter(stage => !stage.pseudo)
              .filter(stage => {
                if (activeFilter === 'won') return stage.role === 'hired';
                if (activeFilter === 'lost') return stage.role === 'lost';
                return true;
              })
              .map(stage => {
              const stageLeads = getLeadsByStage(stage.id);

              return (
                <div
                  className="kpp-col"
                  key={stage.id}
                  style={{ '--sc': stage.colour }}
                  data-over={dragOver === stage.id}
                  onDragOver={(e) => { handleDragOver(e); if (dragOver !== stage.id) setDragOver(stage.id); }}
                  onDragLeave={() => setDragOver((cur) => (cur === stage.id ? null : cur))}
                  onDrop={(e) => handleDrop(e, stage.id)}
                  data-testid={`stage-column-${stage.id}`}
                >
                  <div className="kpp-colhead">
                    <div className="cn"><i />{stage.label}<b>{stageLeads.length}</b></div>
                    <div className="cd">{stage.rule}</div>
                  </div>
                  <div className="kpp-colbody">
                    {stageLeads.length === 0 && <div className="kpp-empty">nothing here</div>}
                    {stageLeads.map(lead => (
                      <article
                        key={lead.id}
                        className="kpp-lead"
                        draggable
                        onDragStart={(e) => handleDragStart(e, lead)}
                        onDragEnd={() => { setDraggedLead(null); setDragOver(null); }}
                        data-dragging={draggedLead?.id === lead.id}
                        onClick={() => navigate(`/pipeline/${lead.id}`)}
                        data-testid={`lead-card-${lead.id}`}
                      >
                        <div className="nm2">{lead.display_name}</div>
                        {lead.email && <div className="em">{lead.email}</div>}
                        {lead.project_type && <div className="tag">{lead.project_type}</div>}
                        <div className="rows">
                          {lead.company && (
                            <div className="rw2"><i>Company</i><span>{lead.company}</span></div>
                          )}
                          {(lead.budget_range || lead.budget) && (
                            <div className="rw2"><i>Budget</i><span>{lead.budget_range || lead.budget}</span></div>
                          )}
                          {lead.phone && (
                            <div className="rw2"><i>Phone</i><span>{lead.phone}</span></div>
                          )}
                          {lead.property_address && (
                            <div className="rw2"><i>Address</i><span>{lead.property_address}</span></div>
                          )}
                        </div>
                        {/* PL-1 — Signed, but no project yet: the one thing
                            left to do, in the card's own idiom. */}
                        {hiredStageIds.includes(lead.stage) && !lead.converted_project_id && (
                          <div className="links">
                            <button
                              type="button"
                              className="need"
                              onClick={(e) => { e.stopPropagation(); moveToSigned(lead, lead.stage); }}
                              data-testid={`create-project-${lead.id}`}
                            >Create project</button>
                          </div>
                        )}
                        {/* Links render only where the lead actually carries a
                            URL. No lead field on this tenant holds one today,
                            so neither link appears — nothing is fabricated. */}
                        {(lead.meeting_url || lead.calendly_url || lead.map_url) && (
                          <div className="links">
                            {(lead.meeting_url || lead.calendly_url) && (
                              <a href={lead.meeting_url || lead.calendly_url} target="_blank" rel="noreferrer"
                                 onClick={(e) => e.stopPropagation()}>Calendar</a>
                            )}
                            {lead.map_url && (
                              <a href={lead.map_url} target="_blank" rel="noreferrer"
                                 onClick={(e) => e.stopPropagation()}>Map</a>
                            )}
                          </div>
                        )}
                        <div className="ft">
                          <span>{lead.source || 'Direct'}</span>
                          <span>{formatDate(lead.created_at)}</span>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <Card className={cardBg}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`border-b ${darkMode ? 'border-[#2A2A2A]' : 'border-gray-200'}`}>
                    <th className={`text-left p-4 text-sm font-medium ${textClass}`}>Lead</th>
                    <th className={`text-left p-4 text-sm font-medium ${textClass}`}>Contact</th>
                    <th className={`text-left p-4 text-sm font-medium ${textClass}`}>Project</th>
                    <th className={`text-left p-4 text-sm font-medium ${textClass}`}>Budget</th>
                    <th className={`text-left p-4 text-sm font-medium ${textClass}`}>Stage</th>
                    <th className={`text-left p-4 text-sm font-medium ${textClass}`}>Source</th>
                    <th className={`text-left p-4 text-sm font-medium ${textClass}`}>Date</th>
                    <th className={`text-left p-4 text-sm font-medium ${textClass}`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map(lead => {
                    const stage = pipelineStages.find(s => s.id === lead.stage);
                    return (
                      <tr 
                        key={lead.id} 
                        className={`border-b ${darkMode ? 'border-[#2A2A2A] hover:bg-[#1A1A1A]' : 'border-gray-100 hover:bg-gray-50'} cursor-pointer`}
                        onClick={() => navigate(`/pipeline/${lead.id}`)}
                        data-testid={`lead-row-${lead.id}`}
                      >
                        <td className="p-4">
                          <div>
                            <p className={`font-medium ${textClass}`}>{lead.display_name}</p>
                            {lead.company && <p className={`text-xs ${mutedClass}`}>{lead.company}</p>}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className={`text-sm ${mutedClass}`}>
                            <div>{lead.email}</div>
                            <div>{lead.phone}</div>
                          </div>
                        </td>
                        <td className="p-4">
                          {lead.project_type && <Badge variant="outline">{lead.project_type}</Badge>}
                          {lead.converted_project_id && (
                            <button
                              type="button"
                              className={`block mt-1 text-xs underline ${mutedClass}`}
                              onClick={(e) => { e.stopPropagation();
                                navigate(`/projects/${lead.converted_project_id}/hub`); }}
                              data-testid={`lead-project-link-${lead.id}`}
                            >Project: {projectNames[lead.converted_project_id] || 'open'}</button>
                          )}
                        </td>
                        <td className={`p-4 ${textClass}`}>{lead.budget_range || lead.budget || '—'}</td>
                        <td className="p-4">
                          <Badge className={`${stage?.color || 'bg-gray-500'} text-white`}>{stage?.label || lead.stage}</Badge>
                        </td>
                        <td className={`p-4 text-sm ${mutedClass}`}>{lead.source || '—'}</td>
                        <td className={`p-4 text-sm ${mutedClass}`}>{formatDate(lead.created_at)}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-1">
                            {lead.stage === 'negotiation' && (
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedLead(lead);
                                  setShowConvertDialog(true);
                                }}
                                className="h-8 text-emerald-600"
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteLead(lead);
                              }}
                              className="h-8"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {filteredLeads.length === 0 && (
                <div className={`text-center py-16 ${mutedClass}`}>
                  <Target className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <h3 className={`text-lg font-medium mb-2 ${textClass}`}>No Leads Found</h3>
                  <p className="text-sm">Try adjusting your search or filter</p>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Empty State - No leads at all */}
        {leads.length === 0 && (
          <div className={`text-center py-16 ${mutedClass}`}>
            <Target className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <h3 className={`text-lg font-medium mb-2 ${textClass}`}>Start Building Your Pipeline</h3>
            <p className="text-sm mb-4">Add your first lead to track potential projects</p>
            <Button onClick={() => setShowNewLeadDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add First Lead
            </Button>
          </div>
        )}

        {/* Calendly Info Card */}
        {leads.some(l => l.source === 'Calendly') && (
          <Card className={`mt-6 ${darkMode ? 'bg-[#1A1A1A]/50 border-[#2A2A2A]' : 'bg-blue-50/50 border-blue-100'}`}>
            <CardContent className="py-4">
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${darkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                  <Calendar className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <h3 className={`font-medium mb-1 ${textClass}`}>Calendly Integration Active</h3>
                  <p className={`text-sm ${mutedClass}`}>
                    Consultation bookings automatically create leads in your pipeline.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Lead Detail Sheet */}
      <Sheet open={showDetailPanel} onOpenChange={setShowDetailPanel}>
        <SheetContent className={`w-[500px] sm:max-w-[500px] ${darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : ''}`}>
          {selectedLead && (
            <>
              <SheetHeader>
                <SheetTitle className={textClass}>{selectedLead.display_name}</SheetTitle>
                <SheetDescription>
                  {selectedLead.company && `${selectedLead.company} • `}
                  Added {formatDate(selectedLead.created_at)}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6 overflow-y-auto max-h-[calc(100vh-120px)] pr-1">
                {/* Stage Progress */}
                <div>
                  <Label className={`text-sm ${mutedClass}`}>Pipeline Stage</Label>
                  <div className="flex items-center gap-1 mt-3">
                    {pipelineStages.filter(s => !s.pseudo && s.id !== 'lost').map((stage, idx) => {
                      const currentIdx = pipelineStages.filter(s => !s.pseudo && s.id !== 'lost').findIndex(s => s.id === selectedLead.stage);
                      const isActive = stage.id === selectedLead.stage;
                      const isPast = idx < currentIdx;
                      
                      return (
                        <button
                          key={stage.id}
                          onClick={() => (hiredStageIds.includes(stage.id)
                            && !selectedLead.converted_project_id
                            ? moveToSigned(selectedLead, stage.id)
                            : updateLeadStage(selectedLead, stage.id))}
                          className={`flex-1 h-2.5 rounded-full transition-all duration-300 hover:opacity-80 ${
                            isActive ? stage.color : isPast ? 'bg-emerald-500' : darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-200'
                          }`}
                          title={stage.label}
                          data-testid={`stage-btn-${stage.id}`}
                        />
                      );
                    })}
                  </div>
                  <div className="flex justify-between mt-1">
                    {pipelineStages.filter(s => !s.pseudo && s.id !== 'lost').map((stage) => (
                      <span 
                        key={stage.id} 
                        className={`text-[10px] ${
                          stage.id === selectedLead.stage ? `font-bold ${stage.textColor}` : mutedClass
                        }`}
                      >
                        {stage.label}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Editable Contact Info */}
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#1A1A1A]' : 'bg-gray-50'}`}>
                  <h4 className={`font-medium mb-3 ${textClass}`}>Contact Information</h4>
                  <div className="space-y-3">
                    <div>
                      <Label className={`text-xs ${mutedClass}`}>Name</Label>
                      <Input value={selectedLead.name || selectedLead.display_name || ''} data-testid="lead-edit-name"
                        className={`mt-1 ${darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}`}
                        onChange={(e) => setSelectedLead(p => ({ ...p, name: e.target.value, display_name: e.target.value }))}
                        onBlur={(e) => { if (e.target.value) saveLeadField(selectedLead, 'name', e.target.value); }} />
                    </div>
                    <div>
                      <Label className={`text-xs ${mutedClass}`}>Email</Label>
                      <Input value={selectedLead.email || ''} data-testid="lead-edit-email"
                        className={`mt-1 ${darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}`}
                        onChange={(e) => setSelectedLead(p => ({ ...p, email: e.target.value }))}
                        onBlur={(e) => saveLeadField(selectedLead, 'email', e.target.value)} />
                    </div>
                    <div>
                      <Label className={`text-xs ${mutedClass}`}>Phone</Label>
                      <Input value={selectedLead.phone || ''} data-testid="lead-edit-phone"
                        className={`mt-1 ${darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}`}
                        onChange={(e) => setSelectedLead(p => ({ ...p, phone: e.target.value }))}
                        onBlur={(e) => saveLeadField(selectedLead, 'phone', e.target.value)} />
                    </div>
                    <div>
                      <Label className={`text-xs ${mutedClass}`}>Property Address</Label>
                      <Input value={selectedLead.property_address || selectedLead.address || ''} data-testid="lead-edit-address"
                        className={`mt-1 ${darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}`}
                        onChange={(e) => setSelectedLead(p => ({ ...p, property_address: e.target.value }))}
                        onBlur={(e) => saveLeadField(selectedLead, 'property_address', e.target.value)} />
                    </div>
                  </div>
                </div>

                {/* Editable Project Details */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className={`text-xs ${mutedClass}`}>Project Type</Label>
                    <Input value={selectedLead.project_type || ''} data-testid="lead-edit-project-type"
                      className={`mt-1 ${darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}`}
                      onChange={(e) => setSelectedLead(p => ({ ...p, project_type: e.target.value }))}
                      onBlur={(e) => saveLeadField(selectedLead, 'project_type', e.target.value)} />
                  </div>
                  <div>
                    <Label className={`text-xs ${mutedClass}`}>Budget</Label>
                    <Input value={selectedLead.budget_range || selectedLead.budget || ''} data-testid="lead-edit-budget"
                      className={`mt-1 ${darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}`}
                      onChange={(e) => setSelectedLead(p => ({ ...p, budget_range: e.target.value }))}
                      onBlur={(e) => saveLeadField(selectedLead, 'budget_range', e.target.value)} />
                  </div>
                  <div>
                    <Label className={`text-xs ${mutedClass}`}>Lead Source</Label>
                    <select value={selectedLead.source || ''} data-testid="lead-edit-source"
                      className={`w-full mt-1 h-9 rounded-md border px-3 text-sm ${darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A] text-white' : 'bg-white border-gray-200'}`}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '__custom__') {
                          const custom = prompt('Enter custom lead source:');
                          if (custom) { setSelectedLead(p => ({ ...p, source: custom })); saveLeadField(selectedLead, 'source', custom); }
                        } else {
                          setSelectedLead(p => ({ ...p, source: val }));
                          saveLeadField(selectedLead, 'source', val);
                        }
                      }}>
                      <option value="">Select source...</option>
                      <option value="Calendly / Website">Calendly / Website</option>
                      <option value="Direct Email">Direct Email</option>
                      <option value="Manual Entry">Manual Entry</option>
                      <option value="Advertising">Advertising</option>
                      <option value="Word of Mouth">Word of Mouth</option>
                      <option value="Referral">Referral</option>
                      <option value="Social Media">Social Media</option>
                      <option value="Repeat Client">Repeat Client</option>
                      {selectedLead.source && !['Calendly / Website','Direct Email','Manual Entry','Advertising','Word of Mouth','Referral','Social Media','Repeat Client','Email Hub','Calendly'].includes(selectedLead.source) && (
                        <option value={selectedLead.source}>{selectedLead.source}</option>
                      )}
                      <option value="__custom__">+ Custom...</option>
                    </select>
                  </div>
                  <div>
                    <Label className={`text-xs ${mutedClass}`}>Consultation</Label>
                    <p className={`text-sm mt-2 ${textClass}`}>
                      {formatDate(selectedLead.consultation_date)}
                    </p>
                  </div>
                </div>

                {/* Editable Notes */}
                <div>
                  <Label className={`text-xs ${mutedClass}`}>Notes</Label>
                  <textarea value={selectedLead.notes || selectedLead.project_description || ''} data-testid="lead-edit-notes"
                    rows={3}
                    className={`w-full mt-1 p-3 rounded-lg border text-sm resize-none ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A] text-white' : 'bg-gray-50 border-gray-200'}`}
                    onChange={(e) => setSelectedLead(p => ({ ...p, notes: e.target.value }))}
                    onBlur={(e) => saveLeadField(selectedLead, 'notes', e.target.value)} />
                </div>

                {/* Actions */}
                <div className={`pt-4 border-t ${darkMode ? 'border-[#2A2A2A]' : 'border-gray-200'}`}>
                  <h4 className={`font-medium mb-3 ${textClass}`}>Quick Actions</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <Button 
                      variant="outline" 
                      className="justify-start"
                      onClick={() => window.location.href = `mailto:${selectedLead.email}`}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Send Email
                    </Button>
                    <Button 
                      variant="outline" 
                      className="justify-start"
                      onClick={() => window.location.href = `tel:${selectedLead.phone}`}
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Call
                    </Button>
                    <Button 
                      variant="outline" 
                      className="justify-start"
                      onClick={() => setShowSignatureDialog(true)}
                    >
                      <FileSignature className="w-4 h-4 mr-2" />
                      Request Signature
                    </Button>
                    <Button 
                      variant="outline" 
                      className="justify-start"
                      onClick={() => toast.info('Print feature coming soon')}
                    >
                      <Printer className="w-4 h-4 mr-2" />
                      Print Agreement
                    </Button>
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="flex gap-2 pt-4">
                  {selectedLead.stage !== 'won' && selectedLead.stage !== 'lost' && (
                    <>
                      <Button 
                        variant="outline" 
                        className="flex-1 text-red-600"
                        onClick={() => archiveLead(selectedLead)}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Mark Lost
                      </Button>
                      {(selectedLead.stage === 'negotiation' || selectedLead.stage === 'proposal') && (
                        <Button 
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => setShowConvertDialog(true)}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Convert to Project
                        </Button>
                      )}
                    </>
                  )}
                  {selectedLead.stage === 'won' && selectedLead.converted_project_id && (
                    <Button 
                      className="flex-1"
                      onClick={() => navigate(`/projects/${selectedLead.converted_project_id}/hub`)}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View Project
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* New Lead Dialog */}
      <Dialog open={showNewLeadDialog} onOpenChange={setShowNewLeadDialog}>
        <DialogContent className={`max-w-md ${darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : ''}`}>
          <DialogHeader>
            <DialogTitle className={textClass}>Add New Lead</DialogTitle>
            <DialogDescription>
              Enter lead information to add to your pipeline
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Name *</Label>
                <Input 
                  placeholder="John Smith"
                  value={newLead.name}
                  onChange={(e) => setNewLead({ ...newLead, name: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input 
                  type="email"
                  placeholder="john@example.com"
                  value={newLead.email}
                  onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input 
                  placeholder="(555) 555-5555"
                  value={newLead.phone}
                  onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div className="col-span-2">
                <Label>Company</Label>
                <Input 
                  placeholder="Company name"
                  value={newLead.company}
                  onChange={(e) => setNewLead({ ...newLead, company: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <Label>Lead Source</Label>
                <Select value={newLead.source} onValueChange={(v) => setNewLead({ ...newLead, source: v })}>
                  <SelectTrigger className={inputClass}>
                    <SelectValue placeholder="Select source..." />
                  </SelectTrigger>
                  <SelectContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                    {LEAD_SOURCES.map(src => (
                      <SelectItem key={src} value={src} className={darkMode ? 'text-[#E5E2DD] focus:bg-[#2A2A2A]' : ''}>
                        {src}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Project Type</Label>
                <Select value={newLead.project_type} onValueChange={(v) => setNewLead({ ...newLead, project_type: v })}>
                  <SelectTrigger className={inputClass}>
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                    {PROJECT_TYPES.map(type => (
                      <SelectItem key={type} value={type} className={darkMode ? 'text-[#E5E2DD] focus:bg-[#2A2A2A]' : ''}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Budget Range</Label>
                <Select value={newLead.budget_range} onValueChange={(v) => setNewLead({ ...newLead, budget_range: v })}>
                  <SelectTrigger className={inputClass}>
                    <SelectValue placeholder="Select range..." />
                  </SelectTrigger>
                  <SelectContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                    {BUDGET_RANGES.map(range => (
                      <SelectItem key={range} value={range} className={darkMode ? 'text-[#E5E2DD] focus:bg-[#2A2A2A]' : ''}>
                        {range}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Property Address</Label>
                <Input 
                  placeholder="123 Main St, City, State"
                  value={newLead.property_address}
                  onChange={(e) => setNewLead({ ...newLead, property_address: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Textarea 
                  placeholder="Initial notes about this lead..."
                  value={newLead.notes}
                  onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                  className={inputClass}
                  rows={3}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewLeadDialog(false)}>
              Cancel
            </Button>
            <Button onClick={createLead} data-testid="create-lead-btn">
              Add Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Project Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent className={darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : ''}>
          <DialogHeader>
            <DialogTitle className={textClass}>Convert Lead to Project</DialogTitle>
            <DialogDescription>
              This will create a new project and contact for this lead.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className={`rounded-lg p-4 space-y-2 ${darkMode ? 'bg-[#1A1A1A]' : 'bg-gray-50'}`}>
              <p className={`text-sm ${textClass}`}>
                <strong>Client:</strong> {selectedLead?.display_name}
              </p>
              <p className={`text-sm ${textClass}`}>
                <strong>Email:</strong> {selectedLead?.email}
              </p>
              <p className={`text-sm ${textClass}`}>
                <strong>Project Type:</strong> {selectedLead?.project_type || 'Not specified'}
              </p>
              <p className={`text-sm ${textClass}`}>
                <strong>Budget:</strong> {selectedLead?.budget_range || selectedLead?.budget || 'Not specified'}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => convertToProject(selectedLead)}
              data-testid="confirm-convert-btn"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Create Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signature Request Dialog (placeholder) */}
      <Dialog open={showSignatureDialog} onOpenChange={setShowSignatureDialog}>
        <DialogContent className={darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : ''}>
          <DialogHeader>
            <DialogTitle className={textClass}>Request Signature</DialogTitle>
            <DialogDescription>
              Send a document for electronic or handwritten signature
            </DialogDescription>
          </DialogHeader>

          <div className="py-6 space-y-4">
            <div className={`p-4 rounded-lg border-2 border-dashed cursor-pointer hover:border-[#BC5D41] transition-colors ${darkMode ? 'border-[#2A2A2A]' : 'border-gray-200'}`}>
              <div className="text-center">
                <PenTool className={`w-8 h-8 mx-auto mb-2 ${mutedClass}`} />
                <p className={`font-medium ${textClass}`}>E-Signature</p>
                <p className={`text-xs ${mutedClass}`}>Send for digital signature</p>
              </div>
            </div>
            <div className={`p-4 rounded-lg border-2 border-dashed cursor-pointer hover:border-[#BC5D41] transition-colors ${darkMode ? 'border-[#2A2A2A]' : 'border-gray-200'}`}>
              <div className="text-center">
                <Printer className={`w-8 h-8 mx-auto mb-2 ${mutedClass}`} />
                <p className={`font-medium ${textClass}`}>Print for Handwritten</p>
                <p className={`text-xs ${mutedClass}`}>Print and scan signed copy</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignatureDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PL-1 — the New Project wizard, pre-filled from the lead. Cancel leaves
          the lead in Signed with its "Create project" button; Create links the
          two and the card leaves the board. */}
      <QuickStartWizard
        open={showProjectWizard}
        prefill={wizardPrefill}
        sourceLead={wizardLead}
        onClose={() => { setShowProjectWizard(false); setWizardLead(null); }}
        onProjectCreated={(project) => {
          if (wizardLead) {
            setLeads(leads.map(l => (l.id === wizardLead.id
              ? { ...l, converted_project_id: project.id } : l)));
            setProjectNames({ ...projectNames, [project.id]: project.name });
            if (selectedLead?.id === wizardLead.id) {
              setSelectedLead({ ...selectedLead, converted_project_id: project.id });
            }
          }
        }}
      />
    </div>
    </AppShell>
  );
}
